<template>
  <div
    :class="[
      'flex items-center justify-center rounded-full font-semibold text-white select-none',
      sizeClasses,
      ringClass
    ]"
    :style="{ backgroundColor: bgColor }"
    :title="name"
  >
    {{ initials }}
    <div
      v-if="showStatusDot && status"
      :class="[
        'absolute rounded-full ring-2 ring-white',
        statusDotClasses,
        statusColorClasses
      ]"
    ></div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  name: {
    type: String,
    required: true
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value)
  },
  status: {
    type: String,
    default: null,
    validator: (value) => [null, 'present', 'absent', 'online', 'offline'].includes(value)
  },
  showStatusDot: {
    type: Boolean,
    default: false
  },
  ring: {
    type: Boolean,
    default: false
  }
});

// Generate initials from name
const initials = computed(() => {
  if (!props.name) return '?';

  const parts = props.name.trim().split(/\s+/);

  if (parts.length === 1) {
    // Single name: take first 2 characters
    return parts[0].substring(0, 2).toUpperCase();
  }

  // Multiple names: take first letter of first and last name
  const first = parts[0][0];
  const last = parts[parts.length - 1][0];

  return (first + last).toUpperCase();
});

// Generate consistent color from name
const bgColor = computed(() => {
  const colors = [
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#06B6D4', // cyan
    '#F97316', // orange
    '#14B8A6', // teal
    '#6366F1', // indigo
  ];

  // Generate hash from name
  let hash = 0;
  for (let i = 0; i < props.name.length; i++) {
    hash = props.name.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Get color index
  const index = Math.abs(hash) % colors.length;
  return colors[index];
});

// Size classes
const sizeClasses = computed(() => {
  const sizes = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-lg'
  };
  return sizes[props.size] || sizes.md;
});

// Ring class
const ringClass = computed(() => {
  return props.ring ? 'ring-2 ring-white shadow-sm' : '';
});

// Status dot size based on avatar size
const statusDotClasses = computed(() => {
  const sizes = {
    xs: 'w-1.5 h-1.5 -bottom-0 -right-0',
    sm: 'w-2 h-2 -bottom-0 -right-0',
    md: 'w-3 h-3 -bottom-0.5 -right-0.5',
    lg: 'w-3.5 h-3.5 -bottom-0.5 -right-0.5',
    xl: 'w-4 h-4 -bottom-1 -right-1'
  };
  return sizes[props.size] || sizes.md;
});

// Status dot color
const statusColorClasses = computed(() => {
  const colors = {
    present: 'bg-green-400',
    absent: 'bg-red-400',
    online: 'bg-green-400',
    offline: 'bg-gray-400'
  };
  return colors[props.status] || 'bg-gray-400';
});
</script>

<style scoped>
/* Ensure proper positioning for status dot */
.relative {
  position: relative;
}
</style>
